import { applySnapshot, flow, getSnapshot, types } from "mobx-state-tree";

import createTableStore, { DEFAULT_PAGE_SIZE } from "./templates/TableStore";

import api from "services/api";

import { TagDictionaryItem } from "./dictionary/TagDictionary";

import { getConvertedDurationHoursMins } from "utils/calculation";
import { getNextPublishStatus } from "utils/publish";
import { ClaimModel } from "./ClaimStore";
import { RestrictionModel } from "./RestrictionStore";
import { HealthConditionModel } from "./HealthConditionStore";

import {
  CategoryModel,
  PUBLISH_STATUSES,
  STRUCTURE_TYPES,
} from "constants/models";
import { FILTER_COLUMNS } from "constants/filterConstants";
import { isEmptyObject } from "utils";
import { MSURL } from "utils/ms";

const pathNutrition = "/nutrition/api/v1";
export const RecipeModel = types
  .model({
    id: types.number,
    title: types.string,
    imageUrl: types.maybe(types.string),
    order: types.maybe(types.number),
    day: types.maybe(types.number),
    duration: types.maybe(types.number),
    publishStatus: types.enumeration(Object.values(PUBLISH_STATUSES)),
  })
  .views(self => ({
    get durationText() {
      return getConvertedDurationHoursMins(self.duration);
    },
  }));

export const IngredientsModel = types.model({
  title: types.string,
  unit: types.maybe(types.string),
});

export const MacroNutrientsModel = types.model({
  carbs: types.maybe(types.number),
  fiber: types.maybe(types.number),
  fat: types.maybe(types.number),
  protein: types.maybe(types.number),
  calories: types.maybe(types.number),
  sugar: types.maybe(types.number),
  saturatedFat: types.maybe(types.number),
  transFat: types.maybe(types.number),
  cholesterol: types.maybe(types.number),
  sodium: types.maybe(types.number),
  vitaminA: types.maybe(types.number),
  vitaminC: types.maybe(types.number),
  calcium: types.maybe(types.number),
  iron: types.maybe(types.number),
});

export const RecipeDetailsModel = types
  .model({
    id: types.number,
    title: types.string,
    instruction: types.maybe(types.string),
    imageUrl: types.maybe(types.string),
    categories: types.array(CategoryModel),
    macronutrients: types.maybe(MacroNutrientsModel),
    ingredients: types.array(IngredientsModel),
    duration: types.maybe(types.number),
    tags: types.array(TagDictionaryItem),
    publishStatus: types.enumeration(Object.values(PUBLISH_STATUSES)),
    claims: types.maybe(types.array(ClaimModel)),
    restrictions: types.maybe(types.array(RestrictionModel)),
    healthConditions: types.maybe(types.array(HealthConditionModel)),
  })
  .views(self => ({
    get durationHoursMins() {
      if (!self.duration) {
        return { hours: "", minutes: "" };
      }

      const hours = Math.trunc(self.duration / (60 * 60));
      const minutes = Math.trunc(self.duration / 60) - hours * 60;

      return {
        hours: hours === 0 ? "" : hours,
        minutes: minutes === 0 ? "" : minutes,
      };
    },
  }))
  .views(self => ({
    get durationText() {
      return getConvertedDurationHoursMins(self.duration);
    },

    get formData() {
      const uniqueCategoriesMap = new Map();
      self.categories.map(item => uniqueCategoriesMap.set(item.id, item));
      const categoryArray = Array.from(uniqueCategoriesMap.values());

      return {
        ...self,
        file: self.imageUrl,
        durationHoursMins: self.durationHoursMins,
        category: [
          ...categoryArray.filter(
            item => item.structureType === STRUCTURE_TYPES.WEEK,
          ),
          ...categoryArray.filter(
            item => item.structureType === STRUCTURE_TYPES.STANDARD,
          ),
        ].map(({ id, title, structureType }) => ({
          id,
          name: title,
          structureType,
        })),
        claims: self.claims,
        restrictions: self.restrictions,
        healthConditions: self.healthConditions,
      };
    },
  }));

const ContentNutritionRecipesStore = createTableStore({
  fetch: flow(function*({ params }) {
    return api.get(`/food/recipe/list`, params);
  }),
  initialOrder: { column: "created", desc: true },
  itemModel: RecipeModel,
  searchBy: "search",
})
  .props({
    details: types.maybe(RecipeDetailsModel),
    isMoreLoading: types.optional(types.boolean, false),
    isRecipeLoading: types.optional(types.boolean, false),
    isRecipeManaging: types.optional(types.boolean, false),
    isRecipePublishing: types.optional(types.boolean, false),
    isRecipeCopying: types.optional(types.boolean, false),
    isRecipeTaggingLoading: types.optional(types.boolean, false),
    isTagGenerationLoading: types.optional(types.boolean, false),
  })
  .views(self => ({
    get hasMoreInfo() {
      return !!self.totalPages && self.page !== self.totalPages;
    },

    get recipesOptions() {
      const { items } = getSnapshot(self);
      return items.map(({ id, title, duration, imageUrl, publishStatus }) => ({
        label: title,
        value: {
          id,
          imageUrl,
          title,
          duration: getConvertedDurationHoursMins(duration),
          publishStatus,
        },
      }));
    },
  }))
  .actions(self => ({
    updateRecipe(id: number, data: any) {
      const filtersContainNewStatus =
        isEmptyObject(self.filters) ||
        self.filters[FILTER_COLUMNS.PUBLISH_STATUS]?.includes(
          data.publishStatus,
        );

      if (!filtersContainNewStatus) {
        const filteredItems = self.items.filter(item => item.id !== id);
        applySnapshot(self, { ...self, items: filteredItems });
      } else {
        const index = self.items.findIndex(item => item.id === id);
        self.items[index] = { ...self.items[index], ...data };
      }
    },
  }))
  .actions(self => ({
    fetchMoreRecipes: flow(function*() {
      self.isMoreLoading = true;

      try {
        if (self.hasMoreInfo) {
          const { orderBy, orderByDesc } = self.orderContext || {};

          self.applyPage(self.page + 1);

          const { items } = yield api.get(`/food/recipe/list`, {
            size: DEFAULT_PAGE_SIZE,
            page: self.page,
            search: self.search,
            orderBy,
            orderByDesc,
            ...self.filters,
          });

          applySnapshot(self, {
            ...self,
            items: self.items.concat(items),
          });
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isMoreLoading = false;
      }
    }),

    fetchRecipeTagging: flow(function*(id?: number) {
      try {
        self.isRecipeTaggingLoading = true;
        const taggingDetails = yield api.get(
          `${pathNutrition}/recipe/${id}/tagging`,
          undefined,
          undefined,
          MSURL,
        );
        applySnapshot(self, {
          ...self,
          details: {
            ...self.details,
            ...taggingDetails,
          },
        });
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isRecipeTaggingLoading = false;
      }
    }),

    fetchRecipeDetails: flow(function*(id?: number) {
      self.isRecipeLoading = true;

      try {
        const details = yield api.get(`/food/recipe/${id}`);

        applySnapshot(self, {
          ...self,
          details,
        });
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isRecipeLoading = false;
      }
    }),

    createRecipe: flow(function*(payload: any) {
      self.isRecipeManaging = true;

      try {
        const { id } = yield api.postFormData(`/food/recipe`, payload);
        return id;
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isRecipeManaging = false;
      }
    }),

    generateTagging: flow(function*({ id }: { id?: number }) {
      try {
        self.isTagGenerationLoading = true;
        const response = yield api.post(
          `${pathNutrition}/recipe/${id}/generate-claim`,
          {},
          undefined,
          MSURL,
        );
        return response?.success;
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isTagGenerationLoading = false;
      }
    }),

    editRecipeInfo: flow(function*(payload: any) {
      self.isRecipeManaging = true;

      try {
        return yield api.put(`/food/recipe/${payload.id}`, payload);
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isRecipeManaging = false;
      }
    }),

    editRecipeImage: flow(function*(payload: any) {
      self.isRecipeManaging = true;

      try {
        return yield api.putFormData(
          `/food/recipe/${payload.id}/photo/upload`,
          payload,
        );
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isRecipeManaging = false;
      }
    }),

    archiveRecipe: flow(function*(id: number) {
      self.isRecipeManaging = true;

      try {
        return yield api.post(`/food/recipe/${id}/archive`, {});
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isRecipeManaging = false;
      }
    }),

    updatePublishStatus: flow(function*(id: number, status: string) {
      self.isRecipePublishing = true;

      try {
        const newStatus = getNextPublishStatus(status);
        if (newStatus) {
          const { success } = yield api.post(`/food/recipe/${id}/status`, {
            status: newStatus,
          });
          if (success) {
            self.updateRecipe(id, { publishStatus: newStatus });
          }
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isRecipePublishing = false;
      }
    }),

    copyRecipe: flow(function*(id: number) {
      self.isRecipeCopying = true;

      try {
        yield api.post(`/food/recipe/${id}/copy`, {});
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isRecipeCopying = false;
      }
    }),

    resetDetails() {
      applySnapshot(self, { ...self, details: undefined });
    },
  }));

export const ContentNutritionArchivedRecipesStore = createTableStore({
  fetch: flow(function*({ params }) {
    return api.get(`/food/recipe/archived/list`, params);
  }),
  initialOrder: { column: "created", desc: true },
  itemModel: RecipeModel,
  searchBy: "search",
})
  .props({
    isManaging: types.optional(types.boolean, false),
    isMoreLoading: types.optional(types.boolean, false),
  })
  .views(self => ({
    get hasMoreInfo() {
      return !!self.totalPages && self.page !== self.totalPages;
    },

    get options() {
      const { items } = getSnapshot(self);
      return items.map(({ id, title, duration, imageUrl, publishStatus }) => ({
        label: title,
        value: {
          id,
          imageUrl,
          title,
          duration: getConvertedDurationHoursMins(duration),
          publishStatus,
        },
      }));
    },
  }))
  .actions(self => ({
    fetchMore: flow(function*() {
      self.isMoreLoading = true;

      try {
        if (self.hasMoreInfo) {
          self.applyPage(self.page + 1);

          const { items } = yield api.get(`/food/recipe/archived/list`, {
            size: DEFAULT_PAGE_SIZE,
            page: self.page,
          });

          applySnapshot(self, {
            ...self,
            items: [...self.items, ...items],
          });
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isMoreLoading = false;
      }
    }),

    restore: flow(function*(payload: any) {
      self.isManaging = true;

      try {
        return yield api.post(`/food/recipe/restore`, payload);
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isManaging = false;
      }
    }),
  }))
  .create();

export default ContentNutritionRecipesStore.create();
